import { LitElement } from 'lit'
import { customElement } from 'lit/decorators.js'
import { GetPrices } from 'components/ordering/async-price/api/get-prices.js'
import { executePriceRequest } from 'components/ordering/async-price/api/price-executor.js'
import { createContext, provide } from '@lit/context'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { Debouncer, DEBOUNCER } from '@cloudsuite/mosaic-shared/scripts/debounce.js'
import { PriceDTO, PriceError, PricePerProduct } from 'components/ordering/async-price/api/price-dto.js'


export type AsyncPriceSubscriber = { productId: string, callback: (priceDto: PriceDTO) => void }
export type SubscribeAsyncPrice = (element: Element, { productId, callback }: AsyncPriceSubscriber) => void
export const subscribeAsyncPriceContext = createContext<SubscribeAsyncPrice>(Symbol('SUBSCRIBE_ASYNC_PRICE_CONTEXT'))

export type UnsubscribeAsyncPrice = (element: Element) => void
export const unsubscribeAsyncPriceContext = createContext<UnsubscribeAsyncPrice>(Symbol('UNSUBSCRIBE_ASYNC_PRICE_CONTEXT'))


@customElement('csc-async-price-context')
export class CSCAsyncPriceContext extends LitElement {
  protected override createRenderRoot = () => this

  @inject(DEBOUNCER)
  private debounce!: Debouncer
  private debounceDelay: number = 500

  private readonly observer: IntersectionObserver
  private readonly subscribers: Map<Element, AsyncPriceSubscriber> = new Map()

  private readonly productIdsToFetch: Set<string> = new Set()
  private readonly cache: Map<string, PriceDTO> = new Map()

  constructor() {
    super()
    this.observer = new IntersectionObserver(this.handleIntersection, { threshold: 0.1 })
    this.debouncedFetchPrices = this.debounce.debounce(this.fetchPrices, this.debounceDelay, { noLeading: true })
  }

  @provide({ context: subscribeAsyncPriceContext })
  public subscribe: SubscribeAsyncPrice = (element: Element, { productId, callback }: AsyncPriceSubscriber) => {
    if (this.callBackWithCache({ productId, callback })) return

    this.observer.observe(element)
    this.subscribers.set(element, { productId, callback })
  }

  @provide({ context: unsubscribeAsyncPriceContext })
  public unsubscribe: UnsubscribeAsyncPrice = (element: Element) => {
    this.observer.unobserve(element)
    this.subscribers.delete(element)
  }

  private handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return

      const asyncPrice = entry.target
      const subscription = this.subscribers.get(asyncPrice)

      if (subscription === undefined) return

      const { productId, callback } = subscription
      if (this.callBackWithCache({ productId, callback })) return

      this.productIdsToFetch.add(productId)
      this.debouncedFetchPrices()

      this.observer.unobserve(asyncPrice)
    })
  }

  private readonly debouncedFetchPrices: () => void
  private fetchPrices: () => void = () => {
    const getPrices = new GetPrices({ productIds: [...this.productIdsToFetch] })
    this.productIdsToFetch.clear()

    executePriceRequest(getPrices).then(this.handlePriceResponse)
  }

  private handlePriceResponse = (result: PricePerProduct | PriceError) => {
    if (result instanceof PriceError) return console.error(result.status, result.json)

    Object.entries(result).forEach(([productId, priceDTO]) => this.cache.set(productId, priceDTO))
    this.subscribers.forEach(this.callBackWithCache)
  }

  private callBackWithCache = ({ productId, callback }: AsyncPriceSubscriber) => {
    const priceDto = this.cache.get(productId)
    if (priceDto === undefined) return false

    callback(priceDto)
    return true
  }
}
